import { BADGE_STAT_KEYS, type BadgeStatKey, type BadgeStats } from 'features/catalog/badge-definitions';
import { rarityOrderIndex, rarityPoints, type RarityName } from 'features/catalog/rarity';
import { type CpuDifficulty, DIFFICULTY_SEARCH, HEURISTIC, POSITION, SEARCH } from 'features/battle/ai-config';
import { bestNextTurnBoostStat, nextUnit, reduceBattle } from 'features/battle/engine';
import { canDraw, playablePiles, revealableCards } from 'features/battle/queries';
import {
  cpuDrives,
  cardEffects,
  isPileOpen,
  liveBoardBadges,
  livePileCard,
  cardLegalPiles,
  cardRarity,
  cardStatGain,
  cardStats,
  cardTotalStats,
  battleFirstPile,
  battlePileCount,
  canRemoveLeftmostPile,
  emptyUnlockedPileCount,
  isCardLegalForPile,
  isPileDisabled,
  isRarityUnlocked,
  nonemptyPileCount,
  opponentOf,
  rarityHigherBy,
  stateConditionHolds,
  pileCard,
  prestigeIsShielded,
  neighborActivatePrestige,
  shouldActivateOnReveal,
  statLeadIsIgnored,
  opponentSwapPilesFor,
  prestigeToWin,
  swapPilesFor,
  totalStats,
} from 'features/battle/rules';
import {
  BATTLE_ACTIONS_PER_TURN,
  BATTLE_MAX_HAND,
  BATTLE_PILE_COUNT,
  BADGE_STAT_MAX,
  PILE_MAX_RARITY,
  type BattleChoice,
  type CardId,
  type BattlePlayer,
  type BattleCondition,
  type BattleInput,
  type BattleOp,
  type BattleState,
  type CardFilter,
  type PileIndex,
  type PlayerId,
} from 'features/battle/types';

const matchesFilter = (state: BattleState, cardId: string, filter: CardFilter): boolean => {
  if (filter.kind === 'any') return true;
  if (filter.kind === 'rarity') return cardRarity(state, cardId) === filter.rarity;
  if (filter.kind === 'not_rarity') return cardRarity(state, cardId) !== filter.rarity;
  if (filter.kind === 'min_rarity')
    return rarityOrderIndex(cardRarity(state, cardId)) <= rarityOrderIndex(filter.rarity);
  const stats = cardStats(state, cardId);
  if (filter.kind === 'stat') return stats[filter.stat] > 0;
  if (filter.kind === 'any_stat') return filter.stats.some((stat) => stats[stat] > 0);
  return filter.stats.every((stat) => stats[stat] > 0);
};

const cardValue = (state: BattleState, cardId: string): number => {
  const stats = cardStats(state, cardId);
  const total = BADGE_STAT_KEYS.reduce((sum, key) => sum + stats[key], 0);
  return total + rarityPoints[cardRarity(state, cardId)];
};

const raritySlot = (rarity: RarityName): PileIndex => {
  const index = PILE_MAX_RARITY.indexOf(rarity);
  return (index < 0 ? 0 : index) as PileIndex;
};

const skipsOwnNextAction = (ops: readonly BattleOp[]): boolean =>
  ops.some((op) => op.kind === 'skip_actions_next_turn' && op.who !== 'opponent' && op.count > 0);

const boardStat = (state: BattleState, player: PlayerId, stat: BadgeStatKey, skipPile?: PileIndex): number => {
  let value = 0;
  state.players[player].piles.forEach((cardId, index) => {
    if (index === skipPile || cardId == null) return;
    value += cardStats(state, cardId)[stat];
  });
  return value;
};

const leadsAfterReveal = (
  state: BattleState,
  player: PlayerId,
  cardId: string,
  pile: PileIndex,
  stat: BadgeStatKey
): boolean =>
  boardStat(state, player, stat, pile) + cardStats(state, cardId)[stat] > boardStat(state, opponentOf(player), stat);

const conditionHolds = (
  state: BattleState,
  actor: PlayerId,
  condition: BattleCondition,
  sourcePile: PileIndex | null
): boolean | null => {
  const foe = opponentOf(actor);
  const self = state.players[actor];
  const other = state.players[foe];

  const shared = stateConditionHolds(state, actor, condition, sourcePile, matchesFilter);
  if (shared !== undefined) return shared;
  if (condition.kind === 'neither_deck_empty') {
    const remains = (player: BattlePlayer) => player.deck.length - cardsThatLand(player, 1) > 0;
    return remains(self) && remains(other);
  }
  if (condition.kind === 'opponent_board_badge') {
    return liveBoardBadges(state, other).some(({ cardId }) => matchesFilter(state, cardId, condition.filter));
  }
  if (condition.kind === 'discarded_rarities_match') {
    if (!self.hand.length || !other.hand.length) return false;
    return null;
  }
  if (condition.kind === 'other_action_this_turn') {
    const sourceId = sourcePile == null ? null : pileCard(self, sourcePile);
    const revealed = self.revealedThisTurn ?? [];
    const played = self.playedThisTurn ?? [];
    return revealed.some((id) => id !== sourceId) || played.some((id) => id !== sourceId);
  }
  if (condition.kind === 'first_action') return (self.actionsTaken ?? 0) === 0;
  if (condition.kind === 'second_action') return (self.actionsTaken ?? 0) === 1;
  if (condition.kind === 'badge_revealed_or_played_this_turn') {
    const sourceId = sourcePile == null ? null : pileCard(self, sourcePile);
    const revealed = self.revealedThisTurn ?? [];
    const played = self.playedThisTurn ?? [];
    return [...revealed, ...played].some(
      (cardId) => (!condition.excludeSource || cardId !== sourceId) && matchesFilter(state, cardId, condition.filter)
    );
  }
  if (condition.kind === 'effect_played_this_turn') {
    const sourceId = sourcePile == null ? null : pileCard(self, sourcePile);
    return (self.playedThisTurn ?? []).some(
      (cardId) => cardId !== sourceId && cardEffects(state, cardId).some((effect) => effect.stat === condition.stat)
    );
  }
  if (condition.kind === 'returned_own_badge') return nonemptyPileCount(self, state) > 0;
  if (condition.kind === 'stat_gain_reaches') {
    const sourceId = sourcePile == null ? null : pileCard(self, sourcePile);
    if (!sourceId) return false;
    const gained = cardStatGain(state, sourceId)?.[condition.stat] ?? 0;
    return cardStats(state, sourceId)[condition.stat] + gained + 1 === condition.total;
  }
  if (condition.kind === 'source_stat_zero') {
    const sourceId = sourcePile == null ? null : pileCard(self, sourcePile);
    if (!sourceId) return false;
    const gained = cardStatGain(state, sourceId)?.[condition.stat] ?? 0;
    return cardStats(state, sourceId)[condition.stat] + gained <= 1;
  }
  return null;
};

const prestigeFromAmount = (
  state: BattleState,
  actor: PlayerId,
  amount: number | 'nonempty_piles' | 'opponent_empty_unlocked_piles'
): number => {
  if (amount === 'nonempty_piles') return nonemptyPileCount(state.players[actor], state);
  if (amount === 'opponent_empty_unlocked_piles') {
    return emptyUnlockedPileCount(
      state.players[opponentOf(actor)],
      state.round,
      battlePileCount(state),
      battleFirstPile(state),
      state
    );
  }
  return amount;
};

/** Both players gain the same Prestige, so the lead changes only when the race ends. */
const sharedPrestigeResult = (state: BattleState, actor: PlayerId, amount: number): 'win' | 'loss' | 'tie' | 'none' => {
  if (amount <= 0) return 'none';
  const needed = prestigeToWin(state);
  const mine = state.players[actor].prestige + amount;
  const theirs = state.players[opponentOf(actor)].prestige + amount;
  if (mine >= needed && mine > theirs) return 'win';
  if (theirs >= needed && theirs > mine) return 'loss';
  if (mine >= needed || theirs >= needed) return 'tie';
  return 'none';
};

const revealableHand = (state: BattleState, player: PlayerId, filter: CardFilter): string[] =>
  state.players[player].hand.filter(
    (cardId) => matchesFilter(state, cardId, filter) && cardLegalPiles(state, cardId, state.round).length > 0
  );

const matchingBoardBadges = (
  state: BattleState,
  player: PlayerId,
  filter?: CardFilter,
  excludeId?: string | null
): number =>
  liveBoardBadges(state, state.players[player]).filter(
    ({ cardId }) => cardId !== excludeId && (!filter || matchesFilter(state, cardId, filter))
  ).length;

const neighborPileIndex = (sourcePile: PileIndex | null, side: 'left' | 'right'): PileIndex | null => {
  if (sourcePile == null) return null;
  const neighbor = sourcePile + (side === 'left' ? -1 : 1);
  if (neighbor < 0 || neighbor > 3) return null;
  return neighbor as PileIndex;
};

const scoreRevealOp = (play: boolean, hits: number): number => {
  if (hits <= 0) return 0;
  return play ? 6 : 3;
};

/** Draws that leave the deck and fit in hand. A full hand or an empty deck lands nothing. */
/** Piles `move_source` may pick, mirroring the engine: the source swaps with any occupant, which must fit back. */
const sourceMovePiles = (state: BattleState, actor: PlayerId, from: PileIndex | null): PileIndex[] =>
  from == null ? [] : swapPilesFor(state, actor, from);

/** Prefer a pile whose neighbors are both occupied after the swap. The pile swapped from stays occupied. */
const bestSourceMovePile = (state: BattleState, actor: PlayerId, from: PileIndex, piles: readonly PileIndex[]) => {
  const self = state.players[actor];
  const occupiedAfter = (neighbor: number): boolean => {
    if (neighbor < 0 || neighbor >= BATTLE_PILE_COUNT) return false;
    return neighbor === from || livePileCard(state, self, neighbor as PileIndex) != null;
  };
  return bestOf(piles, (pile) => (occupiedAfter(pile - 1) && occupiedAfter(pile + 1) ? 1 : 0));
};

const simulateSourceMove = (
  state: BattleState,
  actor: PlayerId,
  from: PileIndex | null
): { state: BattleState; pile: PileIndex } | null => {
  const piles = sourceMovePiles(state, actor, from);
  if (from == null || !piles.length) return null;
  const pile = bestSourceMovePile(state, actor, from, piles);
  const self = state.players[actor];
  const nextPiles = [...self.piles] as BattlePlayer['piles'];
  nextPiles[pile] = self.piles[from];
  nextPiles[from] = self.piles[pile];
  const players = [...state.players] as BattleState['players'];
  players[actor] = { ...self, piles: nextPiles };
  return { state: { ...state, players }, pile };
};

const cardsThatLand = (player: BattlePlayer, count: number): number =>
  Math.min(count, player.deck.length, Math.max(0, BATTLE_MAX_HAND - player.hand.length));

/** An empty unlocked pile the reveal can use. Covering the card that activated the effect does not count. */
const hasOpenRevealPile = (state: BattleState, actor: PlayerId, filter?: CardFilter): boolean => {
  if (filter?.kind === 'rarity' && !isRarityUnlocked(filter.rarity, state.round)) return false;
  return ([0, 1, 2, 3] as PileIndex[]).some(
    (pile) => livePileCard(state, state.players[actor], pile) == null && isPileOpen(state, pile)
  );
};

/** Lower rank is the higher rarity. No discarded card loses to every real card. */
const discardedRarityRank = (state: BattleState, cardId: string | null): number =>
  cardId == null ? Number.POSITIVE_INFINITY : rarityOrderIndex(cardRarity(state, cardId));

/** Chance a random one-card discard from each hand is strictly higher rarity. */
const rarityDiscardOdds = (
  state: BattleState,
  selfHand: readonly string[],
  foeHand: readonly string[]
): { self: number; foe: number } => {
  const selfCards: (string | null)[] = selfHand.length ? [...selfHand] : [null];
  const foeCards: (string | null)[] = foeHand.length ? [...foeHand] : [null];
  let self = 0;
  let foe = 0;
  for (const mine of selfCards) {
    for (const theirs of foeCards) {
      const mineRank = discardedRarityRank(state, mine);
      const theirRank = discardedRarityRank(state, theirs);
      if (mineRank < theirRank) self += 1;
      else if (theirRank < mineRank) foe += 1;
    }
  }
  const total = selfCards.length * foeCards.length;
  return { self: self / total, foe: foe / total };
};

const shareOf = (cardIds: readonly string[], test: (cardId: string) => boolean): number =>
  cardIds.length ? cardIds.filter(test).length / cardIds.length : 0;

/** Share of unordered pairs from `cardIds` that pass `test`. Hidden draws are scored from composition, not order. */
const pairShare = (cardIds: readonly string[], test: (a: string, b: string) => boolean): number => {
  if (cardIds.length < 2) return 0;
  let hits = 0;
  let total = 0;
  for (let i = 0; i < cardIds.length; i += 1) {
    for (let j = i + 1; j < cardIds.length; j += 1) {
      total += 1;
      if (test(cardIds[i], cardIds[j])) hits += 1;
    }
  }
  return total ? hits / total : 0;
};

const shareAStat = (state: BattleState, a: string, b: string): boolean =>
  BADGE_STAT_KEYS.some((key) => cardStats(state, a)[key] > 0 && cardStats(state, b)[key] > 0);

/** Mix a known-probability `if`: `p` for `then`, the rest for `otherwise`. */
const mixBranches = (p: number, thenScore: number, elseScore: number): number => p * thenScore + (1 - p) * elseScore;

/**
 * Chance a draw-dependent condition holds, from the drawing deck's makeup. The deck order is hidden,
 * so this uses composition only. `null` when the condition does not depend on a draw.
 */
const drawnConditionOdds = (
  state: BattleState,
  actor: PlayerId,
  condition: BattleCondition,
  sourcePile: PileIndex | null,
  drawnLanded: number
): number | null => {
  const self = state.players[actor];
  const other = state.players[opponentOf(actor)];
  const deck = self.deck;
  const represented = (player: BattlePlayer) =>
    new Set(liveBoardBadges(state, player).map(({ cardId }) => cardRarity(state, cardId)));
  if (condition.kind === 'last_two_drawn_same_rarity') {
    if (drawnLanded < 2) return 0;
    return pairShare(deck, (a, b) => cardRarity(state, a) === cardRarity(state, b));
  }
  if (condition.kind === 'last_two_drawn_share_stat') {
    if (drawnLanded < 2) return 0;
    return pairShare(deck, (a, b) => shareAStat(state, a, b));
  }
  if (condition.kind === 'last_two_drawn_match_filter') {
    if (drawnLanded < 2) return 0;
    return pairShare(
      deck,
      (a, b) => matchesFilter(state, a, condition.filter) && matchesFilter(state, b, condition.filter)
    );
  }
  if (condition.kind === 'drawn_rarity') return shareOf(deck, (id) => cardRarity(state, id) === condition.rarity);
  if (condition.kind === 'drawn_matches_filter')
    return shareOf(deck, (id) => matchesFilter(state, id, condition.filter));
  if (condition.kind === 'only_self_drawn_matches_filter') {
    if (drawnLanded < 1) return 0;
    return (
      shareOf(deck, (id) => matchesFilter(state, id, condition.filter)) *
      (1 - shareOf(other.deck, (id) => matchesFilter(state, id, condition.filter)))
    );
  }
  if (condition.kind === 'drawn_matches_source_rarity') {
    const sourceId = sourcePile == null ? null : pileCard(self, sourcePile);
    return sourceId ? shareOf(deck, (id) => cardRarity(state, id) === cardRarity(state, sourceId)) : 0;
  }
  if (condition.kind === 'drawn_rarity_represented') {
    const rarities = represented(self);
    return shareOf(deck, (id) => rarities.has(cardRarity(state, id)));
  }
  if (condition.kind === 'drawn_rarity_unrepresented') {
    const rarities = represented(self);
    return shareOf(deck, (id) => !rarities.has(cardRarity(state, id)));
  }
  if (condition.kind === 'drawn_rarity_matches_opponent_board_badge') {
    const rarities = represented(other);
    return shareOf(deck, (id) => rarities.has(cardRarity(state, id)));
  }
  if (condition.kind === 'drawn_rarities_match') {
    if (!deck.length || !other.deck.length) return 0;
    const rarities = new Set([...deck, ...other.deck].map((id) => cardRarity(state, id)));
    let odds = 0;
    rarities.forEach((rarity) => {
      odds +=
        shareOf(deck, (id) => cardRarity(state, id) === rarity) *
        shareOf(other.deck, (id) => cardRarity(state, id) === rarity);
    });
    return odds;
  }
  return null;
};

/**
 * Chance a discard-dependent condition holds, from the relevant hand's makeup. `null` when the
 * condition is not about those discards. Uses the pre-discard hand: this scorer does not mutate.
 */
const discardedConditionOdds = (
  state: BattleState,
  actor: PlayerId,
  condition: BattleCondition,
  selfDiscarded: number,
  foeDiscarded: number
): number | null => {
  const self = state.players[actor];
  const other = state.players[opponentOf(actor)];
  if (condition.kind === 'discarded_rarities_match') {
    if (!selfDiscarded || !foeDiscarded) return 0;
    if (!self.hand.length || !other.hand.length) return 0;
    let p = 0;
    const rarities = new Set([...self.hand, ...other.hand].map((id) => cardRarity(state, id)));
    rarities.forEach((rarity) => {
      p +=
        shareOf(self.hand, (id) => cardRarity(state, id) === rarity) *
        shareOf(other.hand, (id) => cardRarity(state, id) === rarity);
    });
    return p;
  }
  if (condition.kind === 'discarded_self_rarity') {
    if (!selfDiscarded) return 0;
    return shareOf(self.hand, (id) => cardRarity(state, id) === condition.rarity);
  }
  if (condition.kind === 'discarded_rarity') {
    const hits = condition.who === 'opponent' ? foeDiscarded : selfDiscarded;
    if (!hits) return 0;
    const hand = condition.who === 'opponent' ? other.hand : self.hand;
    return shareOf(hand, (id) => cardRarity(state, id) === condition.rarity);
  }
  if (condition.kind === 'discarded_rarity_at_least') {
    const hits = condition.who === 'opponent' ? foeDiscarded : selfDiscarded;
    if (!hits) return 0;
    const hand = condition.who === 'opponent' ? other.hand : self.hand;
    return shareOf(hand, (id) => rarityOrderIndex(cardRarity(state, id)) <= rarityOrderIndex(condition.rarity));
  }
  return null;
};

/** Earlier ops in the same effect may pull badges off the actor's board before a later `if` runs. */
const conditionAfterBounces = (
  state: BattleState,
  actor: PlayerId,
  condition: BattleCondition,
  sourcePile: PileIndex | null,
  selfBounced: number
): boolean | null => {
  if (condition.kind === 'revealed_count' && selfBounced) {
    return nonemptyPileCount(state.players[actor], state) - selfBounced >= condition.atLeast;
  }
  return conditionHolds(state, actor, condition, sourcePile);
};

/**
 * Cost of the opponent's matching badge gaining a stat. Nothing when that pile is empty or the stat is capped;
 * much worse when it hands them the lead in that stat, worse again if that lead lets the badge activate.
 */
const oppositeGainCost = (
  state: BattleState,
  actor: PlayerId,
  stat: BadgeStatKey,
  amount: number,
  sourcePile: PileIndex | null
): number => {
  const foe = opponentOf(actor);
  const target = sourcePile == null ? null : pileCard(state.players[foe], sourcePile);
  if (!target) return 0;
  const added = Math.min(amount, Math.max(0, BADGE_STAT_MAX - cardTotalStats(state, target)[stat]));
  if (!added) return 0;
  const mine = totalStats(state, state.players[actor])[stat];
  const theirs = totalStats(state, state.players[foe])[stat];
  let cost = added * 2;
  if (theirs <= mine && theirs + added >= mine) cost += 8;
  if (theirs <= mine && theirs + added > mine) {
    cost += 6;
    const effect = cardEffects(state, target)[0];
    if (effect?.stat === stat) cost += 12;
  }
  return cost;
};

const scoreOps = (
  state: BattleState,
  actor: PlayerId,
  ops: readonly BattleOp[],
  sourcePile: PileIndex | null,
  rarityOdds?: { self: number; foe: number }
): number => {
  const foe = opponentOf(actor);
  let score = 0;
  let revealed = false;
  let drawnAsked = 0;
  let drawnLanded = 0;
  let selfDiscarded = 0;
  let foeDiscarded = 0;
  let selfBounced = 0;
  for (const [index, op] of ops.entries()) {
    if (op.kind === 'move_source') {
      // Score the rest of the effect from wherever the move lands.
      const moved = simulateSourceMove(state, actor, sourcePile);
      if (moved) return score + scoreOps(moved.state, actor, ops.slice(index + 1), moved.pile, rarityOdds);
    } else if (op.kind === 'gain_prestige') {
      const amount = prestigeFromAmount(state, actor, op.amount);
      if (op.who === 'all') {
        const result = sharedPrestigeResult(state, actor, amount);
        if (result === 'win') score += HEURISTIC.win;
        else if (result === 'loss') score -= HEURISTIC.win;
        else if (result === 'tie') score += HEURISTIC.prestige;
      } else if (op.who === 'opponent') score -= amount * HEURISTIC.prestige;
      else score += amount * HEURISTIC.prestige;
    } else if (op.kind === 'steal_prestige') {
      if (!prestigeIsShielded(state, foe)) score += Math.min(op.amount, state.players[foe].prestige) * HEURISTIC.steal;
    } else if (op.kind === 'lose_prestige') {
      score -= Math.min(op.amount, state.players[actor].prestige) * HEURISTIC.prestige;
    } else if (op.kind === 'gain_prestige_per_drawn') {
      if (drawnAsked > 0) score += HEURISTIC.prestige * 0.6 * (drawnLanded / drawnAsked);
    } else if (op.kind === 'draw' && op.from === 'opponent' && op.who !== 'opponent' && op.who !== 'all') {
      const self = state.players[actor];
      const room = Math.max(0, BATTLE_MAX_HAND - self.hand.length);
      const landed = Math.min(op.count, state.players[foe].deck.length, room);
      drawnAsked = op.count;
      drawnLanded = landed;
      score += landed * 2.5;
    } else if (op.kind === 'draw' || op.kind === 'draw_up_to_tied' || op.kind === 'draw_up_to') {
      const self = state.players[actor];
      const count =
        op.kind === 'draw'
          ? op.count
          : op.kind === 'draw_up_to'
            ? Math.min(op.max, Math.max(0, op.size - self.hand.length), self.deck.length)
            : Math.min(op.max, Math.max(0, state.players[foe].hand.length - self.hand.length));
      const who = op.kind === 'draw' ? (op.who ?? 'self') : 'self';
      const actorLanded = who === 'opponent' ? 0 : cardsThatLand(self, count);
      const foeLanded = who === 'self' ? 0 : cardsThatLand(state.players[foe], count);
      drawnAsked = who === 'opponent' ? 0 : who === 'all' ? count * 2 : count;
      drawnLanded = who === 'all' ? actorLanded + foeLanded : actorLanded;
      if (who === 'opponent') score -= foeLanded * 2.5;
      else if (who === 'all') score -= actorLanded * 0.5;
      else score += actorLanded * 2;
    } else if (op.kind === 'discard') {
      const handSize = (who: PlayerId) => state.players[who].hand.length;
      const selfHits = Math.min(op.count, handSize(actor));
      const foeHits = Math.min(op.count, handSize(foe));
      if (op.who === 'opponent') {
        foeDiscarded += foeHits;
        score += foeHits * 4;
      } else if (op.who === 'all') {
        selfDiscarded += selfHits;
        foeDiscarded += foeHits;
        score += foeHits;
        score -= selfHits * 1.5;
      } else {
        selfDiscarded += selfHits;
        score -= selfHits * 1.5;
      }
    } else if (op.kind === 'discard_drawn') {
      const hits = Math.min(op.count ?? drawnLanded, drawnLanded);
      selfDiscarded += hits;
      score -= hits * 1.5;
    } else if (op.kind === 'discard_deck') {
      score -= Math.min(op.count, state.players[actor].deck.length) * 0.75;
    } else if (op.kind === 'give') {
      // The foe keeps what we give, so cost it at the cheapest cards we'd actually hand over.
      const given = state.players[actor].hand
        .map((cardId) => cardValue(state, cardId))
        .sort((a, b) => a - b)
        .slice(0, op.count);
      score -= given.reduce((sum, value) => sum + 2 + value / 2, 0);
    } else if (op.kind === 'skip_actions_next_turn') {
      score += (op.who === 'opponent' ? 64 : -64) * Math.max(0, op.count);
    } else if (op.kind === 'disable_piles' || op.kind === 'ignore_stat_lead' || op.kind === 'activate_on_reveal') {
      score += 10;
    } else if (op.kind === 'neighbor_activate_prestige') {
      score += op.amount * HEURISTIC.prestige;
    } else if (op.kind === 'remove_leftmost_pile') {
      if (canRemoveLeftmostPile(state)) {
        const pile = battleFirstPile(state) as PileIndex;
        const mine = pileCard(state.players[actor], pile);
        const theirs = pileCard(state.players[foe], pile);
        score += 6;
        if (theirs) score += 8 + cardValue(state, theirs);
        if (mine) score -= cardValue(state, mine);
      }
    } else if (op.kind === 'block_reveal') {
      const blocked = (who: PlayerId) =>
        state.players[who].hand.filter((id) => matchesFilter(state, id, op.filter)).length;
      score += 10 + blocked(foe) * 8 - blocked(actor) * 4;
    } else if (op.kind === 'ai_takeover') {
      // This seat is already the CPU, so the engine no-ops.
    } else if (op.kind === 'shuffle_random_into_decks') {
      // Both get a random card of this rarity. The only edge is drawing it first.
      score += (state.players[foe].deck.length - state.players[actor].deck.length) * 0.4;
    } else if (op.kind === 'set_next_actions') {
      const queued = BATTLE_ACTIONS_PER_TURN + (state.players[actor].extraActions ?? 0);
      score += Math.max(0, op.count - queued) * 64;
    } else if (op.kind === 'bounce_opponent_board_badge') {
      if (matchingBoardBadges(state, foe, op.filter)) score += 8;
    } else if (op.kind === 'swap_source_with_opponent_board') {
      // Trade the source for their best badge that fits here, favoring high Culture so they struggle to swap back.
      const sourceId = sourcePile == null ? null : pileCard(state.players[actor], sourcePile);
      const gains =
        sourcePile == null || !sourceId
          ? []
          : opponentSwapPilesFor(state, actor, sourcePile).map(
              (pile) => swapTakeValue(state, pileCard(state.players[foe], pile) as string) - cardValue(state, sourceId)
            );
      if (gains.length) score += Math.max(...gains);
    } else if (op.kind === 'bounce_opponent_matching_drawn') {
      score += matchingBoardBadges(state, foe) ? 8 : 0;
    } else if (op.kind === 'bounce_self_board_badge') {
      const sourceId = sourcePile == null ? null : pileCard(state.players[actor], sourcePile);
      const matches = liveBoardBadges(state, state.players[actor]).filter(
        ({ cardId }) =>
          (!op.excludeSource || cardId !== sourceId) && (!op.filter || matchesFilter(state, cardId, op.filter))
      );
      if (matches.length) {
        selfBounced += 1;
        if (op.who === 'opponent') score -= 6;
        else {
          // Back in hand, off the board. The CPU gives up the cheapest match, and only when a later payoff earns it.
          const values = matches.map(({ cardId }) => cardValue(state, cardId));
          const lost = op.random ? values.reduce((sum, value) => sum + value, 0) / values.length : Math.min(...values);
          score -= (op.to ?? 'hand') === 'hand' ? lost / 2 : lost;
        }
      }
    } else if (op.kind === 'bounce_source') {
      if (sourcePile != null) selfBounced += 1;
      score += 1;
    } else if (op.kind === 'discard_source') {
      const sourceId = sourcePile == null ? null : pileCard(state.players[actor], sourcePile);
      if (sourceId) score -= cardValue(state, sourceId);
    } else if (op.kind === 'discard_neighbor') {
      const sides = op.side === 'random' ? (['left', 'right'] as const) : [op.side];
      const tops = sides
        .map((side) => neighborPileIndex(sourcePile, side))
        .map((neighbor) => (neighbor == null ? null : pileCard(state.players[actor], neighbor)))
        .filter((top): top is string => !!top);
      // Random picks one occupied neighbor, so weigh the average loss.
      if (tops.length) score -= tops.reduce((sum, top) => sum + cardValue(state, top), 0) / tops.length;
    } else if (op.kind === 'discard_board_badges') {
      const selfMatches = liveBoardBadges(state, state.players[actor]).filter(({ cardId }) =>
        matchesFilter(state, cardId, op.filter)
      );
      const foeHits = state.players[foe].badgeShield ? 0 : matchingBoardBadges(state, foe, op.filter);
      selfDiscarded += selfMatches.length;
      foeDiscarded += foeHits;
      score += foeHits * 8;
      for (const { cardId } of selfMatches) score -= cardValue(state, cardId);
    } else if (op.kind === 'discard_board') {
      for (const { cardId } of liveBoardBadges(state, state.players[actor])) score -= cardValue(state, cardId);
    } else if (op.kind === 'replace_neighbor_from_deck') {
      const neighbor = neighborPileIndex(sourcePile, op.side);
      const current = neighbor == null ? null : pileCard(state.players[actor], neighbor);
      const higher = current ? rarityHigherBy(cardRarity(state, current), op.rarityOffset) : null;
      const hits =
        neighbor == null || !higher
          ? []
          : state.players[actor].deck.filter(
              (cardId) =>
                cardRarity(state, cardId) === higher && isCardLegalForPile(state, cardId, neighbor, state.round)
            );
      if (hits.length) score += 6;
    } else if (op.kind === 'replace_self_from_deck') {
      const sourceId = sourcePile == null ? null : pileCard(state.players[actor], sourcePile);
      if (sourcePile != null && sourceId) {
        const hits = state.players[actor].deck.filter((cardId) =>
          isCardLegalForPile(state, cardId, sourcePile, state.round)
        );
        if (hits.some((cardId) => cardValue(state, cardId) > cardValue(state, sourceId))) score += 6;
        if (op.play && hits.length) score += 6;
      }
    } else if (op.kind === 'upgrade_pile_to_random') {
      const cardId = pileCard(state.players[actor], op.pile);
      if (cardId && cardRarity(state, cardId) === op.rarity) score += 6;
    } else if (op.kind === 'replace_other_piles') {
      const sourceId = sourcePile == null ? null : pileCard(state.players[actor], sourcePile);
      for (const { cardId } of liveBoardBadges(state, state.players[actor])) {
        if (cardId !== sourceId) score -= 3;
      }
      if (!state.players[foe].badgeShield) score += liveBoardBadges(state, state.players[foe]).length * 6;
    } else if (op.kind === 'draw_rarity') {
      if (state.players[actor].deck.some((cardId) => cardRarity(state, cardId) === op.rarity)) score += 6;
    } else if (op.kind === 'reveal_from_decks') {
      const hasOne = (who: PlayerId) =>
        state.players[who].deck.some((cardId) => cardRarity(state, cardId) === op.rarity);
      if (hasOne(actor)) score += 8;
      if (hasOne(foe)) score -= 8;
    } else if (op.kind === 'exchange_self_with_opponent_hand') {
      const hits =
        sourcePile == null
          ? []
          : state.players[foe].hand.filter((cardId) => isCardLegalForPile(state, cardId, sourcePile, state.round));
      if (hits.length) score += 2;
    } else if (op.kind === 'boost_stat') {
      score += 3;
    } else if (op.kind === 'gain_stat') {
      const sourceId = sourcePile == null ? null : pileCard(state.players[actor], sourcePile);
      if (sourceId) {
        const cap = Math.min(op.max ?? BADGE_STAT_MAX, BADGE_STAT_MAX);
        const stats = op.stat ? [op.stat] : BADGE_STAT_KEYS;
        const room = Math.max(
          0,
          ...stats.map((stat) => cap - cardStats(state, sourceId)[stat] - (cardStatGain(state, sourceId)?.[stat] ?? 0))
        );
        score += Math.min(op.amount, room) * 2;
      } else {
        score += op.amount * 2;
      }
    } else if (op.kind === 'lose_stat') {
      score -= op.amount * 2;
    } else if (op.kind === 'opposite_gain_stat') {
      score -= oppositeGainCost(state, actor, op.stat, op.amount, sourcePile);
    } else if (op.kind === 'discard_named_rarity') {
      const rarity = modalRarity(state, state.players[foe].hand);
      const hits = rarity ? state.players[foe].hand.filter((cardId) => cardRarity(state, cardId) === rarity).length : 0;
      foeDiscarded += hits;
      score += hits * 4;
    } else if (op.kind === 'guess_drawn_rarity') {
      const self = state.players[actor];
      const hits = expectedNamedRarityHits(state, self.deck, op.count);
      score += cardsThatLand(self, op.count) * 2;
      if (op.reward === 'prestige') score += hits * HEURISTIC.prestige;
      else if (hits > 0) score += 4 * hits;
    } else if (op.kind === 'scry_draw') {
      if (state.players[actor].deck.length) score += 4;
    } else if (op.kind === 'shield_badges' || op.kind === 'shield_prestige') {
      score += 4;
    } else if (op.kind === 'offer_swap_drawn') {
      score += 2;
    } else if (op.kind === 'discard_self_board_badge') {
      const values = liveBoardBadges(state, state.players[actor]).map(({ cardId }) => cardValue(state, cardId));
      if (values.length) score -= Math.min(...values);
    } else if (op.kind === 'discard_revealed') {
      const tops = liveBoardBadges(state, state.players[actor])
        .map(({ cardId }) => cardId)
        .filter((cardId) => !op.filter || matchesFilter(state, cardId, op.filter));
      const hits = Math.min(op.count, tops.length);
      if (hits) {
        const avg = tops.reduce((sum, cardId) => sum + cardValue(state, cardId), 0) / tops.length;
        score -= avg * hits;
      }
    } else if (op.kind === 'play_other') {
      const extras = playablePiles({ ...state, activePlayer: actor }).filter((pile) => pile !== sourcePile).length;
      if (extras) score += 7;
    } else if (op.kind === 'reveal_hand') {
      const owner = op.who === 'opponent' ? foe : actor;
      const hits = revealableHand(state, owner, op.filter).length;
      const value = scoreRevealOp(op.play, hits);
      if (value) revealed = true;
      score += value;
    } else if (op.kind === 'reveal_hand_neighbor') {
      const neighbor = neighborPileIndex(sourcePile, op.side);
      const open =
        neighbor != null && isPileOpen(state, neighbor) && livePileCard(state, state.players[actor], neighbor) == null;
      const hits = open
        ? state.players[actor].hand.filter((cardId) => cardLegalPiles(state, cardId, state.round).includes(neighbor))
            .length
        : 0;
      const value = scoreRevealOp(op.play, hits);
      if (value) revealed = true;
      score += value;
    } else if (op.kind === 'reveal_drawn' || op.kind === 'reveal_if' || op.kind === 'reveal_unrepresented_drawn') {
      const filter = op.kind === 'reveal_if' ? op.filter : undefined;
      if (hasOpenRevealPile(state, actor, filter)) {
        const value = scoreRevealOp(op.kind === 'reveal_if' && op.play, 1);
        if (value) revealed = true;
        score += value;
      }
    } else if (op.kind === 'swap_random_hand') {
      score += Math.max(0, op.count);
    } else if (op.kind === 'swap_hand') {
      const give = state.players[actor].hand.filter((cardId) => matchesFilter(state, cardId, op.give));
      const take = state.players[foe].hand.filter((cardId) => matchesFilter(state, cardId, op.take));
      const gained = take.reduce((sum, cardId) => sum + cardValue(state, cardId), 0);
      const lost = give.reduce((sum, cardId) => sum + cardValue(state, cardId), 0);
      score += (gained - lost) * 0.4;
    } else if (op.kind === 'reveal_unique_filter_drawn') {
      if (hasOpenRevealPile(state, actor, op.filter)) score += 2;
    } else if (op.kind === 'deck_bottom_drawn') {
      score += 0.5;
    } else if (op.kind === 'cycle_hand') {
      const self = state.players[actor];
      const discarded = self.hand.length;
      const landed = Math.min(discarded + op.extra, self.deck.length, BATTLE_MAX_HAND);
      // Net cards gained; with an empty deck this just throws the hand away.
      score += landed - discarded + (landed ? 1 : 0);
    } else if (op.kind === 'if') {
      if (op.condition.kind === 'effect_revealed') {
        score += scoreOps(state, actor, revealed ? op.then : (op.otherwise ?? []), sourcePile, rarityOdds);
      } else if (op.condition.kind === 'discarded_at_least') {
        const hits =
          op.condition.who === 'all'
            ? selfDiscarded + foeDiscarded
            : op.condition.who === 'opponent'
              ? foeDiscarded
              : selfDiscarded;
        const branch = hits >= op.condition.count ? op.then : (op.otherwise ?? []);
        score += scoreOps(state, actor, branch, sourcePile, rarityOdds);
      } else if (op.condition.kind === 'discarded_self_higher' || op.condition.kind === 'discarded_opponent_higher') {
        const hands = rarityOdds ?? rarityDiscardOdds(state, state.players[actor].hand, state.players[foe].hand);
        const selfWins = op.condition.kind === 'discarded_self_higher';
        const winP = selfWins ? hands.self : hands.foe;
        const rest = 1 - winP;
        const winOdds = selfWins ? { self: 1, foe: 0 } : { self: 0, foe: 1 };
        const restOdds = selfWins
          ? { self: 0, foe: rest > 0 ? hands.foe / rest : 0 }
          : { self: rest > 0 ? hands.self / rest : 0, foe: 0 };
        score += mixBranches(
          winP,
          scoreOps(state, actor, op.then, sourcePile, winOdds),
          scoreOps(state, actor, op.otherwise ?? [], sourcePile, restOdds)
        );
      } else if (drawnConditionOdds(state, actor, op.condition, sourcePile, drawnLanded) != null) {
        const p = drawnConditionOdds(state, actor, op.condition, sourcePile, drawnLanded) ?? 0;
        score += mixBranches(
          p,
          scoreOps(state, actor, op.then, sourcePile, rarityOdds),
          scoreOps(state, actor, op.otherwise ?? [], sourcePile, rarityOdds)
        );
      } else if (discardedConditionOdds(state, actor, op.condition, selfDiscarded, foeDiscarded) != null) {
        const p = discardedConditionOdds(state, actor, op.condition, selfDiscarded, foeDiscarded) ?? 0;
        score += mixBranches(
          p,
          scoreOps(state, actor, op.then, sourcePile, rarityOdds),
          scoreOps(state, actor, op.otherwise ?? [], sourcePile, rarityOdds)
        );
      } else {
        const known = conditionAfterBounces(state, actor, op.condition, sourcePile, selfBounced);
        if (known === true) score += scoreOps(state, actor, op.then, sourcePile, rarityOdds);
        else if (known === false) score += scoreOps(state, actor, op.otherwise ?? [], sourcePile, rarityOdds);
        else {
          score += mixBranches(
            0.5,
            scoreOps(state, actor, op.then, sourcePile, rarityOdds),
            scoreOps(state, actor, op.otherwise ?? [], sourcePile, rarityOdds)
          );
        }
      }
    }
  }
  return score;
};

const expectedPrestige = (
  state: BattleState,
  actor: PlayerId,
  ops: readonly BattleOp[],
  sourcePile: PileIndex | null,
  rarityOdds?: { self: number; foe: number }
): number => {
  const foe = opponentOf(actor);
  let gained = 0;
  let revealed = false;
  let drawnAsked = 0;
  let drawnLanded = 0;
  let selfDiscarded = 0;
  let foeDiscarded = 0;
  let selfBounced = 0;
  for (const [index, op] of ops.entries()) {
    if (op.kind === 'move_source') {
      const moved = simulateSourceMove(state, actor, sourcePile);
      if (moved) return gained + expectedPrestige(moved.state, actor, ops.slice(index + 1), moved.pile, rarityOdds);
    } else if (op.kind === 'gain_prestige' && op.who !== 'opponent' && op.who !== 'all') {
      gained += prestigeFromAmount(state, actor, op.amount);
    } else if (op.kind === 'steal_prestige') {
      if (!prestigeIsShielded(state, foe)) gained += Math.min(op.amount, state.players[foe].prestige);
    } else if (op.kind === 'lose_prestige') {
      gained -= Math.min(op.amount, state.players[actor].prestige);
    } else if (op.kind === 'draw' && op.from === 'opponent' && op.who !== 'opponent' && op.who !== 'all') {
      const self = state.players[actor];
      const room = Math.max(0, BATTLE_MAX_HAND - self.hand.length);
      drawnAsked = op.count;
      drawnLanded = Math.min(op.count, state.players[foe].deck.length, room);
    } else if (op.kind === 'draw' || op.kind === 'draw_up_to_tied' || op.kind === 'draw_up_to') {
      const self = state.players[actor];
      const count =
        op.kind === 'draw'
          ? op.count
          : op.kind === 'draw_up_to'
            ? Math.min(op.max, Math.max(0, op.size - self.hand.length), self.deck.length)
            : Math.min(op.max, Math.max(0, state.players[foe].hand.length - self.hand.length));
      const who = op.kind === 'draw' ? (op.who ?? 'self') : 'self';
      drawnAsked = who === 'opponent' ? 0 : who === 'all' ? count * 2 : count;
      drawnLanded =
        who === 'opponent'
          ? 0
          : who === 'all'
            ? cardsThatLand(self, count) + cardsThatLand(state.players[foe], count)
            : cardsThatLand(self, count);
    } else if (op.kind === 'gain_prestige_per_drawn') {
      if (drawnAsked > 0) gained += 0.7 * (drawnLanded / drawnAsked);
    } else if (op.kind === 'discard_named_rarity') {
      const rarity = modalRarity(state, state.players[foe].hand);
      if (rarity)
        foeDiscarded += state.players[foe].hand.filter((cardId) => cardRarity(state, cardId) === rarity).length;
    } else if (op.kind === 'guess_drawn_rarity' && op.reward === 'prestige') {
      gained += expectedNamedRarityHits(state, state.players[actor].deck, op.count);
    } else if (op.kind === 'discard_drawn') {
      selfDiscarded += Math.min(op.count ?? drawnLanded, drawnLanded);
    } else if (op.kind === 'discard') {
      const selfHits = Math.min(op.count, state.players[actor].hand.length);
      const foeHits = Math.min(op.count, state.players[foe].hand.length);
      if (op.who === 'opponent') foeDiscarded += foeHits;
      else if (op.who === 'all') {
        selfDiscarded += selfHits;
        foeDiscarded += foeHits;
      } else selfDiscarded += selfHits;
    } else if (op.kind === 'discard_board_badges') {
      selfDiscarded += matchingBoardBadges(state, actor, op.filter);
      if (!state.players[foe].badgeShield) foeDiscarded += matchingBoardBadges(state, foe, op.filter);
    } else if (op.kind === 'bounce_self_board_badge') {
      const sourceId = sourcePile == null ? null : pileCard(state.players[actor], sourcePile);
      if (matchingBoardBadges(state, actor, op.filter, op.excludeSource ? sourceId : undefined)) selfBounced += 1;
    } else if (op.kind === 'bounce_source') {
      if (sourcePile != null) selfBounced += 1;
    } else if (op.kind === 'reveal_hand') {
      const owner = op.who === 'opponent' ? foe : actor;
      if (revealableHand(state, owner, op.filter).length) revealed = true;
    } else if (op.kind === 'reveal_hand_neighbor') {
      const neighbor = neighborPileIndex(sourcePile, op.side);
      const open =
        neighbor != null && isPileOpen(state, neighbor) && livePileCard(state, state.players[actor], neighbor) == null;
      if (
        open &&
        state.players[actor].hand.some((cardId) => cardLegalPiles(state, cardId, state.round).includes(neighbor))
      ) {
        revealed = true;
      }
    } else if (op.kind === 'if') {
      if (op.condition.kind === 'effect_revealed') {
        gained += expectedPrestige(state, actor, revealed ? op.then : (op.otherwise ?? []), sourcePile, rarityOdds);
      } else if (op.condition.kind === 'discarded_at_least') {
        const hits =
          op.condition.who === 'all'
            ? selfDiscarded + foeDiscarded
            : op.condition.who === 'opponent'
              ? foeDiscarded
              : selfDiscarded;
        const branch = hits >= op.condition.count ? op.then : (op.otherwise ?? []);
        gained += expectedPrestige(state, actor, branch, sourcePile, rarityOdds);
      } else if (op.condition.kind === 'discarded_self_higher' || op.condition.kind === 'discarded_opponent_higher') {
        const hands = rarityOdds ?? rarityDiscardOdds(state, state.players[actor].hand, state.players[foe].hand);
        const selfWins = op.condition.kind === 'discarded_self_higher';
        const winP = selfWins ? hands.self : hands.foe;
        const rest = 1 - winP;
        const winOdds = selfWins ? { self: 1, foe: 0 } : { self: 0, foe: 1 };
        const restOdds = selfWins
          ? { self: 0, foe: rest > 0 ? hands.foe / rest : 0 }
          : { self: rest > 0 ? hands.self / rest : 0, foe: 0 };
        gained += mixBranches(
          winP,
          expectedPrestige(state, actor, op.then, sourcePile, winOdds),
          expectedPrestige(state, actor, op.otherwise ?? [], sourcePile, restOdds)
        );
      } else if (drawnConditionOdds(state, actor, op.condition, sourcePile, drawnLanded) != null) {
        const p = drawnConditionOdds(state, actor, op.condition, sourcePile, drawnLanded) ?? 0;
        gained += mixBranches(
          p,
          expectedPrestige(state, actor, op.then, sourcePile, rarityOdds),
          expectedPrestige(state, actor, op.otherwise ?? [], sourcePile, rarityOdds)
        );
      } else if (discardedConditionOdds(state, actor, op.condition, selfDiscarded, foeDiscarded) != null) {
        const p = discardedConditionOdds(state, actor, op.condition, selfDiscarded, foeDiscarded) ?? 0;
        gained += mixBranches(
          p,
          expectedPrestige(state, actor, op.then, sourcePile, rarityOdds),
          expectedPrestige(state, actor, op.otherwise ?? [], sourcePile, rarityOdds)
        );
      } else {
        const known = conditionAfterBounces(state, actor, op.condition, sourcePile, selfBounced);
        if (known === true) gained += expectedPrestige(state, actor, op.then, sourcePile, rarityOdds);
        else if (known === false) gained += expectedPrestige(state, actor, op.otherwise ?? [], sourcePile, rarityOdds);
        else {
          gained += mixBranches(
            0.5,
            expectedPrestige(state, actor, op.then, sourcePile, rarityOdds),
            expectedPrestige(state, actor, op.otherwise ?? [], sourcePile, rarityOdds)
          );
        }
      }
    }
  }
  return gained;
};

/** States are never mutated, so values that depend only on the state are cached against it. */
const statTotalsCache = new WeakMap<BattleState, [BadgeStats, BadgeStats]>();
const playableCache = new WeakMap<BattleState, number>();
const potentialCache = new WeakMap<BattleState, Map<number, number>>();

/** `hasStatLead`, with both boards' totals worked out once per state. The search asks it for every badge it scores. */
const leadsStat = (state: BattleState, player: PlayerId, stat: BadgeStatKey): boolean => {
  let totals = statTotalsCache.get(state);
  if (!totals) {
    totals = [totalStats(state, state.players[0]), totalStats(state, state.players[1])];
    statTotalsCache.set(state, totals);
  }
  return totals[player][stat] > totals[opponentOf(player)][stat];
};

const canPlayStat = (state: BattleState, player: PlayerId, stat: BadgeStatKey): boolean =>
  leadsStat(state, player, stat) || statLeadIsIgnored(state);

const playableExpectedPrestige = (state: BattleState): number => {
  const cached = playableCache.get(state);
  if (cached != null) return cached;
  const value = computePlayableExpectedPrestige(state);
  playableCache.set(state, value);
  return value;
};

const computePlayableExpectedPrestige = (state: BattleState): number => {
  const actor = state.activePlayer;
  let best = 0;
  for (const pile of playablePiles(state)) {
    const cardId = pileCard(state.players[actor], pile);
    if (!cardId) continue;
    const effect = cardEffects(state, cardId)[0];
    if (!effect?.stat || effect.trigger !== 'Activate' || !canPlayStat(state, actor, effect.stat)) continue;
    best = Math.max(
      best,
      expectedPrestige(state, actor, effect.ops, pile) + neighborActivatePrestige(state, actor, pile)
    );
  }
  return best;
};

/**
 * Expected Prestige from the actor's activatable board badges, skipping `skipPile`. Comparing this before and
 * after a move shows whether it sets up or breaks the rest of the board: neighbor and row conditions,
 * rarity or stat mixes, stat leads, first/second action, and behind/tied/ahead checks.
 */
const boardPotential = (state: BattleState, actor: PlayerId, skipPile: PileIndex): number => {
  let byKey = potentialCache.get(state);
  if (!byKey) {
    byKey = new Map();
    potentialCache.set(state, byKey);
  }
  const key = actor * BATTLE_PILE_COUNT + skipPile;
  const cached = byKey.get(key);
  if (cached != null) return cached;
  let total = 0;
  state.players[actor].piles.forEach((cardId, index) => {
    if (cardId == null || index === skipPile) return;
    const effect = cardEffects(state, cardId)[0];
    if (!effect?.stat || effect.trigger !== 'Activate' || !canPlayStat(state, actor, effect.stat)) return;
    const pile = index as PileIndex;
    total += Math.max(
      0,
      expectedPrestige(state, actor, effect.ops, pile) + neighborActivatePrestige(state, actor, pile)
    );
  });
  byKey.set(key, total);
  return total;
};

const withPlayer = (state: BattleState, actor: PlayerId, patch: Partial<BattlePlayer>): BattleState => {
  const players = [...state.players] as BattleState['players'];
  players[actor] = { ...state.players[actor], ...patch };
  return { ...state, players };
};

const scorePlay = (state: BattleState, pile: PileIndex): number => {
  const actor = state.activePlayer;
  const cardId = pileCard(state.players[actor], pile);
  if (!cardId) return -100;
  const effect = cardEffects(state, cardId)[0];
  if (!effect?.stat || effect.trigger !== 'Activate' || !canPlayStat(state, actor, effect.stat)) return -50;
  const neighborBonus = neighborActivatePrestige(state, actor, pile);
  const effectScore = scoreOps(state, actor, effect.ops, pile) + neighborBonus * HEURISTIC.prestige;
  let score = effectScore + (effectScore > 0 ? 6 : -5);
  const prestige = expectedPrestige(state, actor, effect.ops, pile) + neighborBonus;
  const mine = state.players[actor].prestige;
  const foePrestige = state.players[opponentOf(actor)].prestige;
  const needed = prestigeToWin(state);
  if (mine + prestige >= needed) score += HEURISTIC.win;
  if (prestige > 0) {
    score += prestige * (mine === 0 ? HEURISTIC.hunger : mine < foePrestige ? 8 : 4);
    if (foePrestige > mine) score += prestige * (foePrestige - mine);
    if (Math.max(mine, foePrestige) >= needed - 3) score += prestige * 6;
    if (state.actionsLeft <= 1 && !skipsOwnNextAction(effect.ops)) score += 22;
  }
  // Order matters: gaining now can switch off a behind/tied badge, and acting can switch on a second-action one.
  if (state.actionsLeft >= 2 && mine + prestige < needed) {
    const self = state.players[actor];
    const after = withPlayer(state, actor, {
      prestige: mine + Math.round(prestige),
      playedThisTurn: [...(self.playedThisTurn ?? []), cardId],
      actionsTaken: (self.actionsTaken ?? 0) + 1,
    });
    score += (boardPotential(after, actor, pile) - boardPotential(state, actor, pile)) * HEURISTIC.potential;
  }
  return score;
};

const scoreRevealOnto = (state: BattleState, cardId: string, pile: PileIndex): number => {
  const actor = state.activePlayer;
  const current = pileCard(state.players[actor], pile);
  const effect = cardEffects(state, cardId)[0];
  let score = cardValue(state, cardId);
  if (!current) {
    score += 6;
    const slot = raritySlot(cardRarity(state, cardId));
    if (pile === slot) score += 3;
    else if (pile > slot) score -= pile - slot;
  } else {
    score -= cardValue(state, current) * 2;
    const currentEffect = cardEffects(state, current)[0];
    if (
      currentEffect?.stat &&
      currentEffect.trigger === 'Activate' &&
      canPlayStat(state, actor, currentEffect.stat) &&
      expectedPrestige(state, actor, currentEffect.ops, pile) > 0
    ) {
      score -= 22;
    }
  }
  if (effect?.stat && effect.trigger === 'Ongoing' && leadsAfterReveal(state, actor, cardId, pile, effect.stat)) {
    score += 4 + scoreOps(state, actor, effect.ops, pile) * 0.5;
  }
  const firesAfterReveal =
    effect?.stat != null && (leadsAfterReveal(state, actor, cardId, pile, effect.stat) || statLeadIsIgnored(state));
  const autoActivate =
    effect?.trigger === 'Activate' && shouldActivateOnReveal(state, actor, cardId) && firesAfterReveal;
  if (
    effect?.stat &&
    ((effect.trigger === 'On Reveal' && firesAfterReveal) ||
      autoActivate ||
      (effect.trigger === 'Activate' && firesAfterReveal))
  ) {
    const playScore = scoreOps(state, actor, effect.ops, pile);
    const prestige = expectedPrestige(state, actor, effect.ops, pile);
    const outlook = skipsOwnNextAction(effect.ops) ? Math.max(playScore, 0) : playScore;
    score += 4;
    if (effect.trigger === 'On Reveal' || autoActivate || (prestige > 0 && state.actionsLeft >= 2)) {
      score += outlook * 0.85;
    } else score += Math.min(outlook, 3);
  }
  if (playableExpectedPrestige(state) > 0) {
    score -= state.actionsLeft <= 1 ? 24 : 6;
  }
  const self = state.players[actor];
  const nextPiles = [...self.piles] as BattlePlayer['piles'];
  nextPiles[pile] = cardId;
  const after = withPlayer(state, actor, {
    piles: nextPiles,
    revealedThisTurn: [...(self.revealedThisTurn ?? []), cardId],
    actionsTaken: (self.actionsTaken ?? 0) + 1,
  });
  const delta = boardPotential(after, actor, pile) - boardPotential(state, actor, pile);
  score += delta * (state.actionsLeft >= 2 ? HEURISTIC.potential : HEURISTIC.potential / 2);
  return score;
};

/**
 * Drops piles holding a badge revealed earlier this turn: covering it only discards it, wasting that action and
 * card. Choices that must place a badge keep them when nothing else is legal.
 */
const freshRevealPiles = (
  state: BattleState,
  player: PlayerId,
  piles: readonly PileIndex[],
  { mustPlace = true } = {}
): PileIndex[] => {
  const self = state.players[player];
  const revealed = self.revealedThisTurn ?? [];
  const fresh = piles.filter((pile) => {
    const current = pileCard(self, pile);
    return !current || !revealed.includes(current);
  });
  return fresh.length || !mustPlace ? fresh : [...piles];
};

/** Best pile for the reveal and its score. Each pile is scored once; ties go to the lower pile. */
const bestReveal = (state: BattleState, cardId: string, piles: PileIndex[]): { pile: PileIndex; score: number } => {
  let best = { pile: piles[0], score: Number.NEGATIVE_INFINITY };
  for (const pile of piles) {
    const score = scoreRevealOnto(state, cardId, pile);
    if (score > best.score || (score === best.score && pile < best.pile)) best = { pile, score };
  }
  return best;
};

const bestRevealPile = (state: BattleState, cardId: string, piles: PileIndex[]): PileIndex =>
  bestReveal(state, cardId, piles).pile;

const scoreDraw = (state: BattleState): number => {
  const self = state.players[state.activePlayer];
  if (!self.deck.length) return -20;
  if (playableExpectedPrestige(state) > 0) return state.actionsLeft <= 1 ? -18 : -6;
  if (self.hand.length >= BATTLE_MAX_HAND) return -8;
  if (self.hand.length <= 1) return 6;
  if (self.hand.length <= 2) return 3;
  return 0;
};

const discardPriority = (state: BattleState, cardId: string): number => {
  const locked = cardLegalPiles(state, cardId, state.round).length === 0;
  return (locked ? 20 : 0) - cardValue(state, cardId);
};

/** The rarity that appears most in `cardIds`, or `null` when empty. */
const modalRarity = (state: BattleState, cardIds: readonly string[]): RarityName | null => {
  const counts = new Map<RarityName, number>();
  cardIds.forEach((cardId) => {
    const rarity = cardRarity(state, cardId);
    counts.set(rarity, (counts.get(rarity) ?? 0) + 1);
  });
  return [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;
};

/** Expected matches when naming the deck's most common rarity, then drawing `count` cards. */
const expectedNamedRarityHits = (state: BattleState, cardIds: readonly string[], count: number): number => {
  const draws = Math.min(count, cardIds.length);
  const rarity = modalRarity(state, cardIds);
  if (!draws || !rarity) return 0;
  return draws * shareOf(cardIds, (id) => cardRarity(state, id) === rarity);
};

/** Value of taking a foe badge with a source swap. Taking their Culture makes it harder for them to win Culture and swap back. */
const swapTakeValue = (state: BattleState, cardId: CardId): number =>
  cardValue(state, cardId) + cardTotalStats(state, cardId).culture;

const bestOf = <T>(items: readonly T[], score: (item: T) => number): T =>
  [...items].sort((a, b) => score(b) - score(a))[0];

const resolveChoice = (state: BattleState, pending: BattleChoice): BattleInput | null => {
  if (pending.type === 'choose_pile') {
    const from = state.stack[state.stack.length - 1]?.sourcePile ?? null;
    const pile =
      pending.purpose === 'move' && from != null
        ? bestSourceMovePile(state, pending.player, from, pending.piles)
        : bestRevealPile(state, pending.cardId, freshRevealPiles(state, pending.player, pending.piles));
    return { type: 'resolve', target: { kind: 'pile', pile } };
  }
  if (pending.type === 'choose_hand') {
    const cardId =
      pending.purpose === 'give'
        ? bestOf(pending.cardIds, (id) => -cardValue(state, id))
        : pending.purpose === 'discard' || pending.purpose === 'bottom'
          ? bestOf(pending.cardIds, (id) => discardPriority(state, id))
          : bestOf(pending.cardIds, (id) => {
              const piles = cardLegalPiles(state, id, state.round);
              if (!piles.length) return -50;
              return bestReveal(state, id, freshRevealPiles(state, pending.player, piles)).score;
            });
    return { type: 'resolve', target: { kind: 'hand', cardId } };
  }
  if (pending.type === 'choose_rarity') {
    const named = (cardIds: readonly string[]): RarityName => {
      const rarity = modalRarity(state, cardIds);
      return rarity && pending.rarities.includes(rarity) ? rarity : pending.rarities[0];
    };
    if (pending.purpose === 'drawn') {
      return { type: 'resolve', target: { kind: 'rarity', rarity: named(state.players[pending.player].deck) } };
    }
    return {
      type: 'resolve',
      target: { kind: 'rarity', rarity: named(state.players[opponentOf(pending.player)].hand) },
    };
  }
  if (pending.type === 'choose_deck_card') {
    return {
      type: 'resolve',
      target: { kind: 'deck_card', cardId: bestOf(pending.cardIds, (cardId) => cardValue(state, cardId)) },
    };
  }
  if (pending.type === 'choose_swap_drawn') {
    const swap = cardValue(state, pending.foeCard) > cardValue(state, pending.selfCard);
    return { type: 'resolve', target: { kind: 'swap_drawn', swap } };
  }
  if (pending.type === 'choose_stat') {
    return {
      type: 'resolve',
      target: {
        kind: 'stat',
        stat: bestNextTurnBoostStat(state, pending.player, pending.stats, pending.amount),
      },
    };
  }
  if (pending.type === 'choose_board_badge') {
    const pile =
      pending.purpose === 'play_other'
        ? bestOf(pending.piles, (item) =>
            scorePlay({ ...state, activePlayer: pending.player, phase: 'turn', pending: null }, item)
          )
        : pending.purpose === 'discard_self'
          ? bestOf(pending.piles, (item) => {
              const cardId = pileCard(state.players[pending.owner], item);
              if (!cardId) return -100;
              return -cardValue(state, cardId) + (cardRarity(state, cardId) === 'legendary' ? 2 : 0);
            })
          : pending.purpose === 'swap_source'
            ? bestOf(pending.piles, (item) => {
                const cardId = pileCard(state.players[pending.owner], item);
                return cardId ? swapTakeValue(state, cardId) : -100;
              })
            : pending.purpose === 'bounce_self'
              ? bestOf(pending.piles, (item) => {
                  const cardId = pileCard(state.players[pending.owner], item);
                  if (!cardId) return -100;
                  const effect = cardEffects(state, cardId)[0];
                  const prestige = effect ? expectedPrestige(state, pending.owner, effect.ops, item) : 0;
                  return -cardValue(state, cardId) - prestige * 8;
                })
              : bestOf(pending.piles, (item) => {
                  const cardId = pileCard(state.players[pending.owner], item);
                  if (!cardId) return -100;
                  const effect = cardEffects(state, cardId)[0];
                  const leads = effect?.stat ? canPlayStat(state, pending.owner, effect.stat) : false;
                  const prestige =
                    effect && leads
                      ? expectedPrestige(state, pending.owner, effect.ops, item) +
                        neighborActivatePrestige(state, pending.owner, item)
                      : 0;
                  return cardValue(state, cardId) + prestige * 10;
                });
    return { type: 'resolve', target: { kind: 'board_badge', owner: pending.owner, pile } };
  }
  return null;
};

const turnOptions = (state: BattleState): { score: number; input: BattleInput }[] => {
  if (state.phase !== 'turn' || state.actionsLeft <= 0) return [];

  const options: { score: number; input: BattleInput }[] = [];

  for (const pile of playablePiles(state)) {
    options.push({ score: scorePlay(state, pile), input: { type: 'play', pile } });
  }

  for (const reveal of revealableCards(state)) {
    // A reveal action is optional, so one that could only cover this turn's reveal is never taken.
    const piles = freshRevealPiles(state, state.activePlayer, reveal.piles, { mustPlace: false });
    if (!piles.length) continue;
    const { pile, score } = bestReveal(state, reveal.cardId, piles);
    options.push({
      score,
      input: { type: 'reveal', cardId: reveal.cardId, pile },
    });
  }

  if (canDraw(state)) options.push({ score: scoreDraw(state), input: { type: 'draw' } });
  return options.sort((a, b) => b.score - a.score);
};

const chooseTurnAction = (state: BattleState): BattleInput | null => {
  if (state.phase !== 'turn' || state.actionsLeft <= 0) return null;
  const options = turnOptions(state);
  if (!options.length) return canDraw(state) ? { type: 'draw' } : null;
  return options[0].input;
};

const shuffled = (items: readonly string[], rng: number): { items: string[]; rng: number } => {
  const out = [...items];
  let seed = rng;
  for (let i = out.length - 1; i > 0; i--) {
    const roll = nextUnit(seed);
    seed = roll.rng;
    const j = Math.floor(roll.value * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return { items: out, rng: seed };
};

/**
 * Hide what the CPU cannot know: both deck orders are reshuffled, and the opponent's hand is redrawn from
 * everything they could be holding. Without this the search would peek at the real next draw.
 */
const determinize = (state: BattleState, actor: PlayerId, sample: number): BattleState => {
  const foeId = opponentOf(actor);
  const self = state.players[actor];
  const foe = state.players[foeId];
  let rng = (state.rng ^ Math.imul(sample + 1, 0x9e3779b1)) | 0;
  const mine = shuffled(self.deck, rng);
  rng = mine.rng;
  const theirs = shuffled([...foe.hand, ...foe.deck], rng);
  rng = theirs.rng;
  const players = [...state.players] as BattleState['players'];
  players[actor] = { ...self, deck: mine.items };
  players[foeId] = { ...foe, hand: theirs.items.slice(0, foe.hand.length), deck: theirs.items.slice(foe.hand.length) };
  return { ...state, players, rng, undo: null };
};

/** Actions this seat is queued to take on its next turn, after extra actions and skips. */
const nextTurnActionSlots = (player: BattlePlayer): number =>
  Math.max(0, BATTLE_ACTIONS_PER_TURN + (player.extraActions ?? 0) - player.skipActions);

/** How good the position is for `actor` once their turn ends. */
const evaluatePosition = (state: BattleState, actor: PlayerId): number => {
  if (state.phase === 'game_over') {
    if (state.winner === actor) return POSITION.gameOver;
    return state.winner === 'tie' ? 0 : -POSITION.gameOver;
  }
  const self = state.players[actor];
  const foe = state.players[opponentOf(actor)];
  const board = liveBoardBadges(state, self).reduce((sum, { cardId }) => sum + cardValue(state, cardId), 0);
  // Board Prestige the CPU could cash next turn, read with a fresh action count.
  const nextTurn = withPlayer(state, actor, { actionsTaken: 0, playedThisTurn: [], revealedThisTurn: [] });
  const cashouts: number[] = [];
  self.piles.forEach((cardId, index) => {
    if (cardId == null || isPileDisabled(state, index as PileIndex)) return;
    const effect = cardEffects(state, cardId)[0];
    if (!effect?.stat || effect.trigger !== 'Activate' || !canPlayStat(nextTurn, actor, effect.stat)) return;
    const pile = index as PileIndex;
    cashouts.push(
      Math.max(0, expectedPrestige(nextTurn, actor, effect.ops, pile) + neighborActivatePrestige(nextTurn, actor, pile))
    );
  });
  const live = cashouts.length;
  const potential = cashouts
    .sort((a, b) => b - a)
    .slice(0, nextTurnActionSlots(self))
    .reduce((sum, value) => sum + value, 0);
  return (
    (self.prestige - foe.prestige) * POSITION.prestigeLead +
    potential * POSITION.potential +
    live * POSITION.liveBadge +
    board * POSITION.boardValue +
    Math.min(self.hand.length, POSITION.handCap) * POSITION.handCard +
    (foe.skipActions - self.skipActions + (self.extraActions ?? 0) - (foe.extraActions ?? 0)) * POSITION.skippedAction
  );
};

/**
 * How the CPU feels about the match. `score` is its position minus the opponent's, so a board both sides
 * share does not read as a lead. About 200 per Prestige ahead. It is a utility, not a win chance.
 */
export type CpuMood = 'scared' | 'uneasy' | 'steady' | 'smug' | 'taunting';

export type CpuRead = { score: number; mood: CpuMood };

const READ_SCARED = -400;
const READ_UNEASY = -160;
const READ_SMUG = 160;
const READ_TAUNT = 400;

const moodFor = (score: number): CpuMood => {
  if (score <= READ_SCARED) return 'scared';
  if (score <= READ_UNEASY) return 'uneasy';
  if (score >= READ_TAUNT) return 'taunting';
  if (score >= READ_SMUG) return 'smug';
  return 'steady';
};

export const cpuRead = (state: BattleState, actor: PlayerId): CpuRead => {
  if (state.phase === 'game_over') {
    if (state.winner === actor) return { score: POSITION.gameOver, mood: 'taunting' };
    if (state.winner === 'tie') return { score: 0, mood: 'steady' };
    return { score: -POSITION.gameOver, mood: 'scared' };
  }
  const score = evaluatePosition(state, actor) - evaluatePosition(state, opponentOf(actor));
  return { score, mood: moodFor(score) };
};

/** Every way to answer a choice, for the choice search. */
const choiceOptions = (state: BattleState, pending: BattleChoice): BattleInput[] => {
  const resolve = (target: Extract<BattleInput, { type: 'resolve' }>['target']): BattleInput => ({
    type: 'resolve',
    target,
  });
  if (pending.type === 'choose_pile') {
    const piles = pending.purpose === 'reveal' ? freshRevealPiles(state, pending.player, pending.piles) : pending.piles;
    return piles.map((pile) => resolve({ kind: 'pile', pile }));
  }
  if (pending.type === 'choose_hand') return pending.cardIds.map((cardId) => resolve({ kind: 'hand', cardId }));
  if (pending.type === 'choose_rarity') return pending.rarities.map((rarity) => resolve({ kind: 'rarity', rarity }));
  if (pending.type === 'choose_deck_card')
    return pending.cardIds.map((cardId) => resolve({ kind: 'deck_card', cardId }));
  if (pending.type === 'choose_swap_drawn') return [true, false].map((swap) => resolve({ kind: 'swap_drawn', swap }));
  if (pending.type === 'choose_stat') return pending.stats.map((stat) => resolve({ kind: 'stat', stat }));
  if (pending.type === 'choose_board_badge') {
    return pending.piles.map((pile) => resolve({ kind: 'board_badge', owner: pending.owner, pile }));
  }
  return [];
};

/** Later actions compared when `searchRest` is on. Kept small so the outer search stays cheap. */
const REST_WIDTH = 3;

/**
 * Play `first`, then play both seats until the actor's turn ends and `horizon` more opponent turns have
 * passed (or the game ends). Later actions are greedy unless `searchRest` is on, in which case this seat
 * compares a few options for each leftover action; nested calls always greedy so this cannot recurse.
 */
const playOut = (
  state: BattleState,
  actor: PlayerId,
  first: BattleInput,
  horizon: number,
  searchRest: boolean
): BattleState => {
  // Turn changes to wait for: finish the turn in progress, then the opponent's and the actor's turns in pairs.
  const target = state.activePlayer === actor ? Math.max(1, horizon * 2) : horizon * 2 + 1;
  let next = reduceBattle(state, first);
  let turns = next.activePlayer !== state.activePlayer ? 1 : 0;
  for (let steps = 0; steps < 60 * (target + 1) && next.phase !== 'game_over' && turns < target; steps++) {
    const input = pickPlayOutInput(next, actor, searchRest);
    if (!input) break;
    const after = reduceBattle(next, input);
    if (after === next) break;
    if (after.activePlayer !== next.activePlayer) turns += 1;
    next = after;
  }
  return next;
};

const pickPlayOutInput = (state: BattleState, actor: PlayerId, searchRest: boolean): BattleInput | null => {
  if (state.pending) return resolveChoice(state, state.pending);
  if (!searchRest || state.activePlayer !== actor) return chooseTurnAction(state);
  const options = turnOptions(state).slice(0, REST_WIDTH);
  if (options.length < 2) return options[0]?.input ?? chooseTurnAction(state);
  let best = options[0].input;
  let bestValue = Number.NEGATIVE_INFINITY;
  for (const option of options) {
    const value = evaluatePosition(playOut(state, actor, option.input, 0, false), actor);
    if (value > bestValue) {
      bestValue = value;
      best = option.input;
    }
  }
  return best;
};

/** Rates each candidate by playing it out against `samples` guesses at the hidden cards. */
const searchBest = (
  state: BattleState,
  actor: PlayerId,
  candidates: readonly { score: number; input: BattleInput }[],
  difficulty: CpuDifficulty
): { value: number; input: BattleInput }[] => {
  const { samples, horizon, searchRest } = DIFFICULTY_SEARCH[difficulty];
  // The CPU never sees hidden cards: each sample is one guess at the opponent's hand and both deck orders.
  const worlds = Array.from({ length: samples }, (_, sample) => determinize(state, actor, sample));
  const rate = (world: BattleState, input: BattleInput): number =>
    evaluatePosition(playOut(world, actor, input, horizon, searchRest), actor);
  return candidates
    .map((option) => {
      let value = 0;
      for (const world of worlds) value += rate(world, option.input);
      // A few samples can miss a likely bad outcome, so the heuristic score (which weighs the odds) steadies it.
      return { value: value / worlds.length + option.score * SEARCH.prior, input: option.input };
    })
    .sort((a, b) => b.value - a.value);
};

const searchTurnAction = (state: BattleState, difficulty: CpuDifficulty): BattleInput | null => {
  const { width, margin } = DIFFICULTY_SEARCH[difficulty];
  const ranked = turnOptions(state);
  // Options the heuristic rates far below the best are not worth playing out.
  const options = ranked.slice(0, width).filter((option) => option.score >= ranked[0].score - margin);
  if (options.length < 2) return null;
  return searchBest(state, state.activePlayer, options, difficulty)[0]?.input ?? null;
};

const searchChoice = (state: BattleState, pending: BattleChoice, difficulty: CpuDifficulty): BattleInput | null => {
  const options = choiceOptions(state, pending);
  if (options.length < 2) return null;
  // No quick rating per option here, so the greedy pick gets a small head start to break near-ties.
  const greedy = resolveChoice(state, pending);
  const candidates = options.map((input) => ({
    score: greedy && JSON.stringify(greedy) === JSON.stringify(input) ? SEARCH.choicePrior : 0,
    input,
  }));
  return searchBest(state, pending.player, candidates, difficulty)[0]?.input ?? null;
};

/** When a reveal would be followed by a draw anyway, draw first: it sees one more badge before committing. */
const drawBeforeReveal = (state: BattleState, input: BattleInput | null): BattleInput | null => {
  if (
    input?.type === 'reveal' &&
    state.actionsLeft >= 2 &&
    canDraw(state) &&
    state.players[state.activePlayer].hand.length < BATTLE_MAX_HAND
  ) {
    const afterReveal = reduceBattle(state, input);
    if (!afterReveal.pending && afterReveal.phase === 'turn' && chooseTurnAction(afterReveal)?.type === 'draw') {
      return { type: 'draw' };
    }
  }
  return input;
};

const pickGreedyInput = (state: BattleState): BattleInput | null => {
  if (state.phase === 'game_over') return null;
  if (state.pending) return resolveChoice(state, state.pending);
  return drawBeforeReveal(state, chooseTurnAction(state));
};

/**
 * A slip for the Easy CPU: now and then it takes the second-best action, as long as that rates nearly as well
 * and the best one does not win outright. Seeded from the state, so the same position always plays the same way.
 */
const slip = (state: BattleState, difficulty: CpuDifficulty): BattleInput | null => {
  const { mistakeRate, mistakeMargin } = DIFFICULTY_SEARCH[difficulty];
  if (!mistakeRate || state.pending) return null;
  const roll = nextUnit((state.rng ^ Math.imul(state.round * 8 + state.actionsLeft + 1, 0x85ebca6b)) | 0).value;
  if (roll >= mistakeRate) return null;
  const [best, second] = turnOptions(state);
  if (!best || !second || best.score >= HEURISTIC.win || second.score < best.score - mistakeMargin) return null;
  return second.input;
};

export const pickCpuInput = (state: BattleState, difficulty: CpuDifficulty = 'easy'): BattleInput | null => {
  if (state.phase === 'game_over') return null;
  if (state.pending) {
    const searched = DIFFICULTY_SEARCH[difficulty].searchChoices
      ? searchChoice(state, state.pending, difficulty)
      : null;
    return searched ?? resolveChoice(state, state.pending);
  }
  const slipped = slip(state, difficulty);
  if (slipped) return slipped;
  const searched = searchTurnAction(state, difficulty);
  return searched ? drawBeforeReveal(state, searched) : pickGreedyInput(state);
};

export const shouldCpuAct = (state: BattleState): boolean => {
  if (state.phase === 'game_over') return false;
  if (state.pending) return cpuDrives(state, state.pending.player);
  return state.phase === 'turn' && cpuDrives(state, state.activePlayer);
};
